import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { Shield, Network, Camera, KeyRound, Home, Bell, ArrowRight, ChevronRight, Award, Users, Zap, Clock } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { ui, servicesData, portfolioData } from '../translations';

const ICON_MAP = { Shield, Network, Camera, KeyRound, Home, Bell, Award, Users, Zap, Clock };

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const StatItem = ({ value, label }) => (
  <div className="text-center px-6 py-4">
    <div className="text-3xl font-black text-[#00BCD4] font-heading">{value}</div>
    <div className="text-slate-400 text-sm mt-1 uppercase tracking-wider">{label}</div>
  </div>
);

const ServiceCard = ({ service, t }) => {
  const Icon = ICON_MAP[service.icon] || Shield;
  return (
    <div data-testid={`service-card-${service.id}`} className="card-service group">
      <div className="icon-container mb-5">
        <Icon size={24} className="text-[#00BCD4]" strokeWidth={1.5}/>
      </div>
      <h3 className="text-lg font-bold text-[#0A192F] mb-2 font-heading">{t(service.name)}</h3>
      <p className="text-slate-500 text-sm leading-relaxed mb-4">{t(service.desc)}</p>
      <Link to="/servicii" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F] group-hover:text-[#00BCD4] transition-colors duration-200">
        <span>{t({ ro: 'Află mai mult', en: 'Learn more' })}</span>
        <ChevronRight size={16}/>
      </Link>
    </div>
  );
};

const ProjectCard = ({ project, t }) => (
  <div data-testid={`portfolio-card-${project.id}`} className="group overflow-hidden bg-white border border-slate-200 hover:border-[#00BCD4] transition-all duration-300 hover:-translate-y-1">
    <div className="relative h-52 overflow-hidden">
      <img src={project.image} alt={t(project.title)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent"/>
      <div className="absolute bottom-3 left-4 flex gap-2 flex-wrap">
        {project.tags.slice(0, 2).map((tag, i) => (
          <span key={i} className="text-xs bg-[#00BCD4]/20 border border-[#00BCD4]/40 text-[#00BCD4] px-2 py-0.5">{t(tag)}</span>
        ))}
      </div>
    </div>
    <div className="p-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#00BCD4] font-semibold uppercase tracking-wider">{project.location} · {project.year}</span>
      </div>
      <h3 className="font-bold text-[#0A192F] text-base font-heading mb-2">{t(project.title)}</h3>
      <p className="text-slate-500 text-sm leading-relaxed line-clamp-2">{t(project.desc)}</p>
    </div>
  </div>
);

const HomePage = () => {
  const t = useT();
  const lang = t({ ro: 'ro', en: 'en' });
  useSEO({
    title: lang === 'ro'
      ? 'Bee-Advised | Soluții IT, Securitate & Clădiri Inteligente România'
      : 'Bee-Advised | IT, Security & Smart Building Solutions Romania',
    description: lang === 'ro'
      ? 'Concepem, instalăm și gestionăm infrastructuri IT, securitate cibernetică, CCTV, control acces, sisteme smart home și BMS pentru companii și locuințe din România.'
      : 'We design, install and manage IT, cybersecurity, CCTV, access control, smart home and BMS systems for businesses and residences in Romania.',
    canonical: 'https://www.bee-advised.ro/',
    lang,
  });
  const s1 = useReveal(), s2 = useReveal(), s3 = useReveal(), s4 = useReveal();
  const previewServices = servicesData.slice(0, 6);
  const previewProjects = portfolioData.slice(0, 3);

  const whyFeatures = [
    { icon: Award, title: t(ui.whyUs.f1t), desc: t(ui.whyUs.f1d) },
    { icon: Zap, title: t(ui.whyUs.f2t), desc: t(ui.whyUs.f2d) },
    { icon: Clock, title: t(ui.whyUs.f3t), desc: t(ui.whyUs.f3d) },
    { icon: Users, title: t(ui.whyUs.f4t), desc: t(ui.whyUs.f4d) },
  ];

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex flex-col bg-[#0A192F] hex-pattern overflow-hidden">
        <div className="absolute inset-0"
          style={{ backgroundImage: `linear-gradient(rgba(10,25,47,0.88) 0%, rgba(10,25,47,0.75) 50%, rgba(10,25,47,0.92) 100%), url(https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=85)`, backgroundSize: 'cover', backgroundPosition: 'center' }}/>
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
            <div className="max-w-3xl">
              <div className="section-badge animate-fade-in opacity-0-init">
                <span className="w-8 h-px bg-[#00BCD4]"/>
                {t(ui.hero.badge)}
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-white font-heading tracking-tight leading-none mt-4 animate-fade-in-up opacity-0-init animate-delay-100">
                {t(ui.hero.title1)}<br/>
                <span className="text-[#00BCD4]">{t(ui.hero.title2)}</span>
              </h1>
              <p className="text-lg text-slate-300 mt-6 max-w-xl leading-relaxed animate-fade-in-up opacity-0-init animate-delay-200">
                {t(ui.hero.subtitle)}
              </p>
              <div className="flex flex-wrap gap-4 mt-10 animate-fade-in-up opacity-0-init animate-delay-300">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                  {t(ui.hero.cta1)} <ArrowRight size={16}/>
                </Link>
                <Link to="/servicii" data-testid="hero-cta-secondary" className="btn-primary-outline">
                  {t(ui.hero.cta2)}
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* Stats bar */}
        <div className="relative z-10 border-t border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-700/50">
              <StatItem value="10+" label={t(ui.stats.years)}/>
              <StatItem value="200+" label={t(ui.stats.projects)}/>
              <StatItem value="150+" label={t(ui.stats.clients)}/>
              <StatItem value="99.9%" label={t(ui.stats.uptime)}/>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={s1} className="reveal text-center mb-14">
            <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.servicesSection.badge)}</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.servicesSection.title)}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">{t(ui.servicesSection.subtitle)}</p>
          </div>
          <div ref={s2} className="reveal grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {previewServices.map(s => <ServiceCard key={s.id} service={s} t={t}/>)}
          </div>
          <div className="text-center mt-12">
            <Link to="/servicii" data-testid="view-all-services" className="btn-secondary">
              {t(ui.servicesSection.viewAll)} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={s3} className="reveal text-center mb-14">
            <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.whyUs.badge)}</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.whyUs.title)}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`why-feature-${i}`} className="bg-white border-l-4 border-[#0A192F] p-8 hover:border-[#00BCD4] transition-colors duration-300">
                  <Icon size={28} className="text-[#00BCD4] mb-4" strokeWidth={1.5}/>
                  <h3 className="font-bold text-[#0A192F] text-base font-heading mb-2">{f.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section data-testid="portfolio-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.portfolioSection.badge)}</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.portfolioSection.title)}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">{t(ui.portfolioSection.subtitle)}</p>
          </div>
          <div ref={s4} className="reveal grid grid-cols-1 md:grid-cols-3 gap-6">
            {previewProjects.map(p => <ProjectCard key={p.id} project={p} t={t}/>)}
          </div>
          <div className="text-center mt-12">
            <Link to="/portofoliu" data-testid="view-all-portfolio" className="btn-secondary">
              {t(ui.portfolioSection.viewAll)} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 bg-[#0A192F] hex-pattern">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white font-heading tracking-tight">{t(ui.cta.title)}</h2>
          <p className="text-slate-400 mt-4 text-lg leading-relaxed">{t(ui.cta.subtitle)}</p>
          <div className="flex flex-wrap gap-4 mt-10 justify-center">
            <Link to="/contact" data-testid="cta-btn-primary" className="btn-primary">
              {t(ui.cta.btn1)} <ArrowRight size={16}/>
            </Link>
            <a href="tel:+40744324272" data-testid="cta-btn-phone" className="btn-primary-outline">
              <span>📞</span> {t(ui.cta.btn2)}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
